import React, { useState, useMemo, useEffect, useCallback } from 'react';
import './DiagnosisHeader.scss';
import CalendarDatepicker from '../../components/common/CalendarDatepicker';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { fetchEspDiagnosisData } from '../../services/well-diagnosis/WellDiagnosisService';
import { useNavigate, useParams } from 'react-router-dom';
import { useSettingUrls } from '../../hooks/useSettingUrls';
import { ReactComponent as PreviousStep } from '../../images/prev-arrow.svg';
import { ReactComponent as NextStep } from '../../images/next-arrow.svg';
import { ReactComponent as CalendarLoading } from '../../images/calendar-load.svg';

export interface UrlParams {
  assetId: string;
  assetGroupName: string;
}

const DiagnosisHeader = ({ espDiagnosisData }: { espDiagnosisData: any }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const testSelectDates = espDiagnosisData?.TestSelect ?? undefined;
  const initialTestId = espDiagnosisData?.Id ?? undefined;

  const endDate = useMemo(() => {
    if (!testSelectDates || Object.keys(testSelectDates).length === 0) {
      return new Date(); // fallback to current date
    }
    return new Date(testSelectDates[Object.keys(testSelectDates)[0]]);
  }, [testSelectDates]);

  const wellId = useAppSelector((state: any) => state.WellTestIdSlice?.data?.WellId);

  const startDate = useMemo(() => {
    if (!testSelectDates || Object.keys(testSelectDates).length === 0) {
      return new Date(); // fallback to current date
    }
    return new Date(testSelectDates[Object.keys(testSelectDates)[Object.keys(testSelectDates).length - 1]]);
  }, [testSelectDates]);

  // Get available dates in chronological order
  const availableDates = useMemo(() => {
    if (!testSelectDates || Object.keys(testSelectDates).length === 0) {
      return [];
    }
    return Object.values(testSelectDates)
      .map((dateStr) => new Date(dateStr as string))
      .sort((a, b) => a.getTime() - b.getTime());
  }, [testSelectDates]);

  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // Helper function to find testId based on selected date
  const findWellIdByDate = useCallback(
    (date: Date): string | null => {
      if (!testSelectDates || !date) return null;

      const dateString = date.toISOString().split('T')[0]; // Format as YYYY-MM-DD

      // Find the testId that matches this date
      for (const [testId, testDate] of Object.entries(testSelectDates)) {
        const testDateString = new Date(testDate as string).toISOString().split('T')[0];
        if (testDateString === dateString) {
          return testId;
        }
      }

      return null;
    },
    [testSelectDates],
  );

  // Update selectedDate when data loads
  useEffect(() => {
    if (testSelectDates && Object.keys(testSelectDates).length > 0) {
      if (initialTestId && testSelectDates[initialTestId]) {
        setSelectedDate(new Date(testSelectDates[initialTestId]));
      }
    }
  }, [testSelectDates, initialTestId]);

  const [activeTab, setActiveTab] = useState('');

  const tabs = [
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'detailed-data', label: 'Detailed data' },
    { id: 'test-history', label: 'Test history' },
    { id: 'workover-economics', label: 'Workover economics' },
  ];

  const { pumpCheckerURL } = useSettingUrls();
  const { assetId, assetGroupName } = useParams<keyof UrlParams>() as UrlParams;

  const handleTabClick = useCallback(
    (tabId: string) => {
      setActiveTab(tabId);
      if (tabId === 'recommendations') {
        const baseUrl = window.location.origin;
        window.open(`${baseUrl}/recommendations`, '_blank');
      } else if (tabId === 'test-history') {
        navigate(`/asset/tests/${assetId}/${encodeURIComponent(assetGroupName)}`);
      } else if (tabId === 'workover-economics') {
        window.open(`${pumpCheckerURL}/${assetId}`, '_blank');
      }
    },
    [navigate, assetId, assetGroupName, pumpCheckerURL],
  );

  const handleDateNavigation = useCallback(
    (direction: 'prev' | 'next') => {
      if (!selectedDate || availableDates.length === 0) return;

      const currentIndex = availableDates.findIndex((date) => date.toDateString() === selectedDate.toDateString());

      let newDate: Date | null = null;
      if (direction === 'prev' && currentIndex > 0) {
        newDate = availableDates[currentIndex - 1];
      } else if (direction === 'next' && currentIndex < availableDates.length - 1) {
        newDate = availableDates[currentIndex + 1];
      }

      if (newDate) {
        setSelectedDate(newDate);
        const testId = findWellIdByDate(newDate);
        if (testId) {
          dispatch(fetchEspDiagnosisData({ testId: parseInt(testId), wellId: parseInt(wellId) }));
        }
      }
    },
    [selectedDate, availableDates, findWellIdByDate, dispatch, wellId],
  );

  const isPrevDisabled = useMemo(
    () => !selectedDate || !startDate || selectedDate.toDateString() === startDate.toDateString(),
    [selectedDate, startDate],
  );

  const isNextDisabled = useMemo(
    () => !selectedDate || !endDate || selectedDate.toDateString() === endDate.toDateString(),
    [selectedDate, endDate],
  );

  const handleDateChange = useCallback(
    (value: { startDate: Date | string | null } | null) => {
      if (value?.startDate) {
        const newDate = new Date(value.startDate);
        setSelectedDate(newDate);
        const testId = findWellIdByDate(newDate);
        if (testId) {
          dispatch(fetchEspDiagnosisData({ testId: parseInt(testId), wellId: parseInt(wellId) }));
        }
      }
    },
    [findWellIdByDate, dispatch, wellId],
  );

  const disabledDates = useMemo(() => {
    if (!testSelectDates || !startDate || !endDate) {
      return [];
    }
    const availableDates = Object.values(testSelectDates).map((dateStr) => new Date(dateStr as string));
    const disabledDatesList: string[] = [];

    // Create a date range from start to end date
    const currentDate = new Date(startDate);
    const endDateCopy = new Date(endDate);

    while (currentDate <= endDateCopy) {
      const dateString = currentDate.toISOString().split('T')[0]; // Format as YYYY-MM-DD

      // Check if this date is NOT in available dates
      const isAvailable = availableDates.some(
        (availableDate) => availableDate.toDateString() === currentDate.toDateString(),
      );

      if (!isAvailable) {
        disabledDatesList.push(dateString);
      }

      // Move to next day
      currentDate.setDate(currentDate.getDate() + 1);
    }
    return disabledDatesList;
  }, [testSelectDates, startDate, endDate]);

  return (
    <div className='diagnosis-header'>
      {/* Left side - Breadcrumbs and Date Picker */}
      <div className='diagnosis-header-left'>
        <div className='breadcrumb-section'>
          <span className='breadcrumb-main'>ESP Diagnosis</span>
          <span className='breadcrumb-separator'>{'>'}</span>
          <span className='breadcrumb-item'>Test date</span>
        </div>
        <div className={`date-picker-section ${!testSelectDates ? 'disabled' : ''}`}>
          <button
            className='date-nav-button'
            onClick={() => handleDateNavigation('prev')}
            disabled={isPrevDisabled || !testSelectDates}
            aria-label='Previous date'
          >
            <PreviousStep />
          </button>
          <div className='date-display'>
            {!testSelectDates ? (
              <div className='date-placeholder'>
                <CalendarLoading />
                <span className='placeholder-text'>Loading...</span>
              </div>
            ) : (
              <CalendarDatepicker
                selectedDate={selectedDate}
                setSelectedDate={setSelectedDate}
                datepickerOnChange={handleDateChange}
                minDate={startDate}
                disabledDates={disabledDates}
                disabled={false}
              />
            )}
          </div>

          <button
            className='date-nav-button'
            onClick={() => handleDateNavigation('next')}
            disabled={isNextDisabled || !testSelectDates}
            aria-label='Next date'
          >
            <NextStep />
          </button>
        </div>
      </div>

      {/* Right side - Tabs */}
      <div className='diagnosis-header-right'>
        <div className='tabs-container'>
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabClick(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DiagnosisHeader;
